import { useMutation, useQuery } from '@tanstack/react-query';
import fileDownload from 'js-file-download';
import type { ReactElement } from 'react';
import { TooltipTrigger } from 'react-aria-components';

import { Route } from '..';
import { Download01StrokeIcon, IconButton, Tooltip } from '@/libs/serenity-ui';
import { reportMutations, reportQueries } from '@/resources/report/report.api';

interface DownloadReportIconButtonProps {
  type: string;
}

export function DownloadReportIconButton({ type }: DownloadReportIconButtonProps): ReactElement | null {
  const routeContext = Route.useRouteContext();
  const search = Route.useSearch();

  const reportsQuery = useQuery({
    ...reportQueries.list({ type }),
    enabled: !!type,
  });

  const selectedReport =
    reportsQuery.data?.find((r) => r.csvFileName === search.reportIndex) ??
    reportsQuery.data?.at(-1);

  const hasReports = !reportsQuery.isLoading && !!reportsQuery.data?.length;

  const downloadFileMutation = useMutation({
    ...reportMutations.download,
    onSuccess: (data, variables) => fileDownload(data, `${routeContext.organization.original.name} - ${variables.fileName}`),
  });

  return hasReports && selectedReport ? (
    <TooltipTrigger>
      <IconButton
        aria-label="Download report"
        isDisabled={!selectedReport || downloadFileMutation.isPending}
        size={40}
        variant="ghost"
        onPress={() => downloadFileMutation.mutate({
          type,
          entityType: selectedReport!.entityType,
          fileName: selectedReport!.xlsxFileName ?? selectedReport!.csvFileName,
        })}>
        <Download01StrokeIcon />
      </IconButton>
      <Tooltip>Download report</Tooltip>
    </TooltipTrigger>
  ) : null;
}
